/**
 * i18n 설정 관리
 * 환경변수를 통한 동적 설정 지원
 */

import {
  LanguageCode,
  DEFAULT_LANGUAGE,
  LANGUAGE_CODES,
  REQUIRED_LANGUAGES,
  FALLBACK_LANGUAGES,
} from '../shared/constants/i18n/languages';
import { I18nConfig, FieldConfig } from '../shared/types/i18n/i18nTypes';

/**
 * 환경변수에서 언어 목록을 파싱하는 함수
 */
const parseLanguagesFromEnv = (
  envValue: string | undefined,
  defaultValue: LanguageCode[]
): LanguageCode[] => {
  if (!envValue) return defaultValue;

  const languages = envValue.split(',').map(lang => lang.trim()) as LanguageCode[];
  return languages.filter(lang => LANGUAGE_CODES.includes(lang));
};

/**
 * 필드별 설정 정의
 */
export const FIELD_CONFIG: Record<string, FieldConfig> = {
  title: {
    required: true,
    maxLength: 255,
  },
  synopsis: {
    required: false,
    maxLength: 2000,
  },
  seo_title: {
    required: false,
    maxLength: 255,
  },
  seo_synopsis: {
    required: false,
    maxLength: 99999,
  },
  seo_keyword: {
    required: false,
    maxLength: 99999,
  },
  name: {
    required: true,
    maxLength: 255,
  },
};

/**
 * i18n 설정 객체
 * 환경변수를 통해 동적으로 설정 가능
 */
export const I18N_CONFIG: I18nConfig = {
  // 기본 언어 설정
  defaultLanguage: (process.env.REACT_APP_DEFAULT_LANGUAGE as LanguageCode) || DEFAULT_LANGUAGE,

  // 지원 언어 목록 (환경변수에서 설정 가능)
  supportedLanguages: parseLanguagesFromEnv(
    process.env.REACT_APP_SUPPORTED_LANGUAGES,
    LANGUAGE_CODES
  ),

  // 필수 언어 목록 (환경변수에서 설정 가능)
  requiredLanguages: parseLanguagesFromEnv(
    process.env.REACT_APP_REQUIRED_LANGUAGES,
    REQUIRED_LANGUAGES
  ),

  // 폴백 언어 순서 (환경변수에서 설정 가능)
  fallbackLanguages: parseLanguagesFromEnv(
    process.env.REACT_APP_FALLBACK_LANGUAGES,
    FALLBACK_LANGUAGES
  ),

  // i18n 필드 기능 활성화 여부
  enableI18nFields: process.env.REACT_APP_ENABLE_I18N_FIELDS === 'true',

  // 필드별 설정
  fieldConfig: FIELD_CONFIG,
};

/**
 * 설정 검증 함수
 */
export const validateI18nConfig = (): boolean => {
  const config = I18N_CONFIG;

  // 기본 언어가 지원 언어에 포함되어 있는지 확인
  if (!config.supportedLanguages.includes(config.defaultLanguage)) {
    console.warn(`Default language '${config.defaultLanguage}' is not in the supported languages list.`);
    return false;
  }

  // 필수 언어가 모두 지원 언어에 포함되어 있는지 확인
  const unsupportedRequired = config.requiredLanguages.filter(
    lang => !config.supportedLanguages.includes(lang)
  );
  if (unsupportedRequired.length > 0) {
    console.warn(`Some required languages are not supported: ${unsupportedRequired.join(', ')}`);
    return false;
  }

  // 폴백 언어가 모두 지원 언어에 포함되어 있는지 확인
  const unsupportedFallback = config.fallbackLanguages.filter(
    lang => !config.supportedLanguages.includes(lang)
  );
  if (unsupportedFallback.length > 0) {
    console.warn(`Some fallback languages are not supported: ${unsupportedFallback.join(', ')}`);
    return false;
  }

  return true;
};

/**
 * 런타임에서 설정을 업데이트하는 함수
 */
export const updateI18nConfig = (updates: Partial<I18nConfig>): void => {
  Object.assign(I18N_CONFIG, updates);

  if (!validateI18nConfig()) {
    console.error('i18n configuration validation failed after update.');
  }
};

/**
 * 특정 필드의 설정을 가져오는 함수
 */
export const getFieldConfig = (fieldName: string): FieldConfig => {
  return I18N_CONFIG.fieldConfig[fieldName] || { required: false, maxLength: 255 };
};

/**
 * 언어가 필수 언어인지 확인하는 함수
 */
export const isRequiredLanguage = (language: LanguageCode): boolean => {
  return I18N_CONFIG.requiredLanguages.includes(language);
};

/**
 * 언어가 지원되는지 확인하는 함수
 */
export const isSupportedLanguage = (language: LanguageCode): boolean => {
  return I18N_CONFIG.supportedLanguages.includes(language);
};

// 초기 설정 검증
if (process.env.NODE_ENV !== 'test') {
  validateI18nConfig();
}
